// RehabBadge & RehabScopeCard — surface AI-verified rehab data anywhere
// in the app or on the public report.
//
// RehabBadge        — tiny chip ("🛡 AI-Verified · Medium · $42K-$58K")
//                     drop on lead cards, table rows, pipeline kanban
// RehabScopeCard    — full section ("Rehab Scope · AI Verified")
//                     drop on the public report page below the analysis card

import { ShieldCheck, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";

const TIER_TONE = {
  light:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  heavy:  "bg-orange-100 text-orange-800 border-orange-200",
  gut:    "bg-rose-100 text-rose-800 border-rose-200",
};

const TIER_LABEL = {
  light: "Light", medium: "Medium", heavy: "Heavy", gut: "Gut",
};

const usd = (n) => n == null ? "—" : `$${Number(n).toLocaleString()}`;

// ─── Compact chip — works on pipeline cards + leads table ───────────────
export function RehabBadge({ estimate, compact = false }) {
  if (!estimate?.range_mid) return null;
  const tone = TIER_TONE[estimate.tier] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = TIER_LABEL[estimate.tier] || estimate.tier || "—";

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${tone}`}
        title={`AI-verified rehab scope · ${label} tier · ${usd(estimate.range_low)} - ${usd(estimate.range_high)}`}
        data-testid="rehab-badge-compact"
      >
        <ShieldCheck size={9} className="opacity-80" />
        <span>{label}</span>
        <span className="opacity-60">·</span>
        <span>{usd(estimate.range_mid)}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold border ${tone}`}
      title={`AI-verified rehab scope · ${usd(estimate.range_low)} - ${usd(estimate.range_high)}`}
      data-testid="rehab-badge"
    >
      <ShieldCheck size={11} />
      <span>AI-Verified · {label}</span>
      <span className="opacity-60">·</span>
      <span className="font-mono tabular-nums">{usd(estimate.range_mid)}</span>
    </span>
  );
}

// ─── Confidence ribbon ──────────────────────────────────────────────────
function ConfidenceChip({ level }) {
  const tone = level === "high" ? "bg-emerald-500 text-white" :
               level === "medium" ? "bg-amber-500 text-white" :
               "bg-rose-500 text-white";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${tone}`}>
      <Sparkles size={10} /> {level} confidence
    </span>
  );
}

// ─── Full report section for public/shared property reports ─────────────
export default function RehabScopeCard({ estimate, accent = "#10B981" }) {
  if (!estimate?.range_mid) return null;
  const tone = TIER_TONE[estimate.tier] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = TIER_LABEL[estimate.tier] || estimate.tier || "—";
  const conf = estimate.confidence || "medium";
  const perSystem = estimate.per_system || {};
  const sow = estimate.sow || [];
  const missing = estimate.missing_systems || [];

  return (
    <section
      className="card overflow-hidden p-0"
      data-testid="rehab-scope-card"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      {/* HEADER */}
      <div className="px-6 py-5 flex items-start justify-between gap-3 border-b border-[var(--line)] bg-[var(--bg)]">
        <div className="min-w-0">
          <div className="label-eyebrow flex items-center gap-1.5 mb-1">
            <ShieldCheck size={11} style={{ color: accent }} />
            Rehab Scope · AI-Verified
          </div>
          <h3 className="font-display font-bold text-xl text-[var(--ink)]">
            <span className={`inline-block px-2 py-0.5 rounded-md text-base mr-2 border ${tone}`}>{label} tier</span>
            {usd(estimate.range_mid)}
          </h3>
          <div className="text-sm text-[var(--ink-2)] mt-1">
            Range: <b>{usd(estimate.range_low)}</b> — <b>{usd(estimate.range_high)}</b>
            {estimate.photo_count ? ` · ${estimate.photo_count} photos analyzed` : ""}
          </div>
        </div>
        <ConfidenceChip level={conf} />
      </div>

      {/* TIER REASONING */}
      {estimate.tier_reason && (
        <div className="px-6 py-4 text-sm text-[var(--ink-2)] italic leading-relaxed bg-[var(--bg)]">
          {estimate.tier_reason}
        </div>
      )}

      {/* PER-SYSTEM GRID */}
      {Object.keys(perSystem).length > 0 && (
        <div className="px-6 py-5">
          <div className="label-eyebrow mb-3">Per-system breakdown</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(perSystem).map(([k, v]) => (
              <div
                key={k}
                className="rounded-lg border border-[var(--line)] p-3 bg-white"
                data-testid={`rehab-scope-system-${k}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink-3)] truncate capitalize">{k}</span>
                  <SystemCondChip cond={v.condition} />
                </div>
                <div className="font-display font-bold text-[var(--ink)] text-sm font-mono tabular-nums">{usd(v.est)}</div>
                {v.note && (
                  <div className="text-[11px] text-[var(--ink-2)] mt-1 leading-snug line-clamp-3">{v.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SOW BULLETS */}
      {sow.length > 0 && (
        <div className="px-6 pb-5">
          <div className="label-eyebrow mb-3">Scope of work ({sow.length} items)</div>
          <ul className="grid md:grid-cols-2 gap-x-5 gap-y-1.5">
            {sow.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink)]">
                <CheckCircle2 size={13} className="mt-1 flex-shrink-0" style={{ color: accent }} />
                <span className="leading-snug">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* MISSING SYSTEMS DISCLOSURE */}
      {missing.length > 0 && (
        <div className="mx-6 mb-5 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <b>Couldn&apos;t assess from photos:</b> {missing.join(", ")}. Walk the property to verify.
          </div>
        </div>
      )}

      {/* FOOTER credibility line */}
      <div className="px-6 py-3 bg-[var(--bg)] border-t border-[var(--line)] text-[11px] text-[var(--ink-3)] flex items-center gap-1.5">
        <Sparkles size={10} style={{ color: accent }} />
        <span>Verified by <b className="text-[var(--ink-2)]">iSpeedToClose AI</b> · Powered by Claude Sonnet</span>
      </div>
    </section>
  );
}

function SystemCondChip({ cond }) {
  const tone = cond === "good" ? "bg-emerald-100 text-emerald-700" :
               cond === "fair" ? "bg-amber-100 text-amber-700" :
               cond === "poor" ? "bg-rose-100 text-rose-700" :
               "bg-slate-100 text-slate-600";
  return (
    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-[1px] rounded ${tone}`}>
      {cond || "—"}
    </span>
  );
}
